
import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Fab,
    Paper,
    TextField,
    IconButton,
    Typography,
    List,
    ListItem,
    CircularProgress,
    Collapse
} from '@mui/material';
import {
    Chat as ChatIcon,
    Close as CloseIcon,
    Send as SendIcon,
    SmartToy as RobotIcon
} from '@mui/icons-material';
import { useAuthStore } from '../stores/authStore';

interface Message {
    id: string;
    sender: 'user' | 'ai';
    text: string;
}

interface AiHelperWidgetProps {
    documentTemplateId?: string;
    assignmentToken?: string;
    isWelcomeQuestionnaire?: boolean;
    isOpen?: boolean;
    onClose?: () => void;
    hideFab?: boolean;
}

const AiHelperWidget: React.FC<AiHelperWidgetProps> = ({
    documentTemplateId,
    assignmentToken,
    isWelcomeQuestionnaire,
    isOpen: controlledIsOpen,
    onClose,
    hideFab
}) => {
    const [internalIsOpen, setInternalIsOpen] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const { accessToken } = useAuthStore();

    const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;

    const handleToggle = () => {
        if (controlledIsOpen !== undefined) {
            if (onClose) onClose();
        } else {
            setInternalIsOpen(!internalIsOpen);
        }
    };

    const handleClose = () => {
        if (controlledIsOpen !== undefined) {
            if (onClose) onClose();
        } else {
            setInternalIsOpen(false);
        }
    };

    const [messages, setMessages] = useState<Message[]>([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const handleSend = async () => {
        if (!input.trim() || isLoading) return;

        const userMessage: Message = {
            id: Date.now().toString(),
            sender: 'user',
            text: input
        };

        setMessages(prev => [...prev, userMessage]);
        setInput('');
        setIsLoading(true);

        const aiMessageId = (Date.now() + 1).toString();
        // We delay adding the AI message until we have content to show, preventing empty bubbles

        try {
            const headers: Record<string, string> = {
                'Content-Type': 'application/json'
            };

            if (accessToken) {
                headers['Authorization'] = `Bearer ${accessToken}`;
            }

            const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1'}/ai-helper/stream`, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    documentTemplateId,
                    question: userMessage.text,
                    assignmentToken,
                    isWelcomeQuestionnaire
                })
            });

            if (!response.ok) {
                throw new Error('Failed to fetch response');
            }

            const reader = response.body?.getReader();
            const decoder = new TextDecoder();

            if (!reader) throw new Error('No reader available');

            let accumulatedText = '';
            let isFirstChunk = true;

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                const chunk = decoder.decode(value);
                const lines = chunk.split('\n\n');

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        const dataStr = line.replace('data: ', '');
                        if (dataStr === '[DONE]') break;
                        try {
                            const data = JSON.parse(dataStr);
                            accumulatedText += data.text;

                            if (isFirstChunk) {
                                isFirstChunk = false;
                                setIsLoading(false); // Hide spinner as soon as we have text
                            }

                            // Capture per-iteration constants so the setMessages updater below doesn't
                            // close over loop-reassigned variables (no-loop-func).
                            const textSoFar = accumulatedText;
                            const msgId = aiMessageId;

                            setMessages(prev => {
                                const exists = prev.some(msg => msg.id === msgId);
                                if (exists) {
                                    return prev.map(msg =>
                                        msg.id === msgId ? { ...msg, text: textSoFar } : msg
                                    );
                                } else {
                                    return [...prev, {
                                        id: msgId,
                                        sender: 'ai',
                                        text: textSoFar
                                    }];
                                }
                            });
                        } catch (e) {
                            console.error('Error parsing chunk', e);
                        }
                    }
                }
            }

        } catch (error) {
            console.error('Error sending message:', error);
            setMessages(prev => {
                const exists = prev.some(msg => msg.id === aiMessageId);
                if (exists) {
                    return prev.map(msg =>
                        msg.id === aiMessageId ? { ...msg, text: 'Sorry, I encountered an error. Please try again.' } : msg
                    );
                } else {
                    return [...prev, {
                        id: aiMessageId,
                        sender: 'ai',
                        text: 'Sorry, I encountered an error. Please try again.'
                    }];
                }
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <Box sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999 }}>
                <Collapse in={isOpen} orientation="vertical" unmountOnExit>
                    <Paper
                        elevation={6}
                        sx={{
                            width: 350,
                            height: 500,
                            mb: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            borderRadius: 3,
                            overflow: 'hidden',
                            bgcolor: 'background.paper',
                            border: '1px solid',
                            borderColor: 'divider'
                        }}
                    >
                        {/* Header */}
                        <Box sx={{
                            p: 2,
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <RobotIcon />
                                <Typography variant="subtitle1" fontWeight="bold">AI Assistant</Typography>
                            </Box>
                            <IconButton size="small" onClick={handleClose} sx={{ color: 'inherit' }}>
                                <CloseIcon />
                            </IconButton>
                        </Box>

                        {/* Messages */}
                        <List sx={{
                            flex: 1,
                            overflowY: 'auto',
                            p: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1
                        }}>
                            {messages.length === 0 && (
                                <Box sx={{ textAlign: 'center', mt: 4, color: 'text.secondary' }}>
                                    <RobotIcon sx={{ fontSize: 40, mb: 1, opacity: 0.5 }} />
                                    <Typography variant="body2">
                                        Hi! I can help you fill out this form. Ask me anything about the fields.
                                    </Typography>
                                </Box>
                            )}
                            {messages.map((msg) => (
                                <ListItem
                                    key={msg.id}
                                    disablePadding
                                    sx={{
                                        justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start',
                                        display: 'flex'
                                    }}
                                >
                                    <Box sx={{
                                        maxWidth: '85%',
                                        p: 1.5,
                                        borderRadius: 2,
                                        bgcolor: msg.sender === 'user' ? 'primary.main' : 'grey.100',
                                        color: msg.sender === 'user' ? 'primary.contrastText' : 'text.primary',
                                        borderTopRightRadius: msg.sender === 'user' ? 0 : 2,
                                        borderTopLeftRadius: msg.sender === 'ai' ? 0 : 2
                                    }}>
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {msg.text}
                                        </Typography>
                                    </Box>
                                </ListItem>
                            ))}
                            {isLoading && (
                                <ListItem disablePadding sx={{ justifyContent: 'flex-start' }}>
                                    <Box sx={{ p: 1.5, bgcolor: 'grey.100', borderRadius: 2 }}>
                                        <CircularProgress size={16} />
                                    </Box>
                                </ListItem>
                            )}
                            <div ref={messagesEndRef} />
                        </List>

                        {/* Input */}
                        <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: 1 }}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Ask a question..."
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
                                disabled={isLoading}
                                multiline
                                maxRows={3}
                            />
                            <IconButton
                                color="primary"
                                onClick={handleSend}
                                disabled={!input.trim() || isLoading}
                                sx={{
                                    bgcolor: 'primary.main',
                                    color: 'white',
                                    height: 40,
                                    width: 40,
                                    '&:hover': { bgcolor: 'primary.dark' },
                                    '&.Mui-disabled': { bgcolor: 'action.disabledBackground' }
                                }}
                            >
                                <SendIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </Paper>
                </Collapse>

                {!hideFab && (
                    <Fab
                        color="primary"
                        aria-label="chat"
                        onClick={handleToggle}
                        sx={{
                            boxShadow: 4,
                            width: 60,
                            height: 60
                        }}
                    >
                        {isOpen ? <CloseIcon /> : <ChatIcon />}
                    </Fab>
                )}
            </Box>
        </>
    );
};

export default AiHelperWidget;
